import { useId, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import type { MemberCredential } from "../api/types";

/** Exported for BulkCredentialDialog.tsx, which shows the same per-field copy affordance for several members at once. */
export function CopyableField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  // A generated id, not a slug of `label`: aria-labelledby is a
  // space-delimited list of IDREFs, so an id containing a space (as
  // "Member ID-label" would) silently fails to resolve, leaving the
  // input with no accessible name at all.
  const labelId = useId();

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopyError(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Denied clipboard permission, an insecure context, or no Clipboard
      // API at all: tell the user to copy manually rather than silently
      // doing nothing (they would otherwise assume the value was copied).
      setCopied(false);
      setCopyError(true);
    }
  }

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" sx={{ fontWeight: 700 }} id={labelId}>
        {label}
      </Typography>
      <OutlinedInput
        value={value}
        readOnly
        fullWidth
        // aria-labelledby as a top-level OutlinedInput prop lands on the
        // MuiInputBase-root wrapper div, not the native <input> itself, so
        // the input would have no accessible name; inputProps sets it
        // directly on the <input> instead.
        inputProps={{ "aria-labelledby": labelId }}
        sx={{ fontFamily: "monospace" }}
        endAdornment={
          <InputAdornment position="end">
            {/* Uncontrolled (hover/focus only): forcing it open on copy
                would make MUI switch the Tooltip between uncontrolled and
                controlled modes, which it warns against. The "Copied"
                confirmation is the aria-live text below instead. */}
            <Tooltip title={`Copy ${label.toLowerCase()}`}>
              <IconButton aria-label={`Copy ${label.toLowerCase()}`} onClick={handleCopy} edge="end">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        }
      />
      <Typography
        variant="caption"
        color={copyError ? "error.main" : "success.main"}
        aria-live="polite"
        sx={{ minHeight: "1em" }}
      >
        {copyError
          ? "Could not copy automatically. Select and copy the text manually."
          : copied
            ? `${label} copied.`
            : ""}
      </Typography>
    </Stack>
  );
}

export interface OneTimeCredentialDialogProps {
  open: boolean;
  title: string;
  /** Null closes the dialog and clears its content; never re-fetched or re-shown once cleared (ADR 0008 section 7). */
  credential: MemberCredential | null;
  onClose: () => void;
}

/**
 * The one-time confirmation screen ADR 0007 section 1 and ADR 0008
 * section 7 require after adding a member or resetting a password: the ID
 * and password shown exactly once, with a copy button each, and a clear
 * warning that the password will never be shown again. This password
 * stays valid until the partner changes it themselves (manager decision,
 * 2026-09-11: no forced change), so staff must hand it over carefully, not
 * just quickly: through two separate channels, one for the ID and one for
 * the password. The caller owns `credential` in its own state and must set
 * it back to null on close (see StaffSharingDetailPage.tsx), so nothing
 * here persists it beyond this dialog's lifetime.
 */
export function OneTimeCredentialDialog({
  open,
  title,
  credential,
  onClose,
}: OneTimeCredentialDialogProps) {
  // MUI's Dialog onClose is only ever invoked for "backdropClick" or
  // "escapeKeyDown"; ignoring both on purpose, unlike every other dialog
  // in the app: this one holds a password shown exactly once (ADR 0007
  // section 1, ADR 0008 section 7), so it must not be dismissible by
  // accident before deliberately clicking Done, which calls `onClose`
  // directly instead of going through this prop.
  function ignoreBackdropAndEscape() {
    // Intentionally does nothing.
  }

  return (
    <Dialog
      open={open}
      onClose={ignoreBackdropAndEscape}
      maxWidth="xs"
      fullWidth
      aria-labelledby="credential-dialog-title"
    >
      <DialogTitle id="credential-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Alert severity="warning">
            This password will not be shown again, and it stays valid until the partner changes
            it. Copy both values now, and send them to the partner through two separate channels,
            for example email for the member ID and phone for the password: never send both the
            same way, and never email the password itself.
          </Alert>
          {credential && (
            <>
              <CopyableField label="Member ID" value={credential.id} />
              <CopyableField label="Password" value={credential.password} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" autoFocus>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
